import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { supabase } from '../../config/supabase';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #f9fafb;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  max-width: 400px;
  width: 100%;
`;

const Logo = styled.div`
  width: 64px;
  height: 64px;
  background: #5efba9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 28px;
  margin: 0 auto 24px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin-bottom: 8px;
`;

const Badge = styled.div`
  background: #5efba9;
  color: #1f2937;
  padding: 4px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin: 0 auto 16px;
  display: inline-block;
  width: fit-content;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  outline: none;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  padding-right: 45px;
  width: 100%;
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }

  &:focus {
    outline: none;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: 24px;
`;

const LinkText = styled(Link)`
  color: #5efba9;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #5efba9;
`;

export default function SuperAdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => {
    // Load saved email on component mount
    return localStorage.getItem('superadminRememberedEmail') || '';
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    // Check if there's a saved email
    return !!localStorage.getItem('superadminRememberedEmail');
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message);
        setLoading(false);
        return;
      }

      // Fetch user profile to check role
      if (authData?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, is_admin, is_superadmin')
          .eq('id', authData.user.id)
          .single();

        console.log('SuperAdmin Login Debug:', {
          profile,
          profileError,
          is_superadmin: profile?.is_superadmin,
          is_superadmin_type: typeof profile?.is_superadmin,
          is_admin: profile?.is_admin,
          is_admin_type: typeof profile?.is_admin
        });

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          setError('Failed to verify user permissions. Please contact support.');
          setLoading(false);
          await supabase.auth.signOut(); // Sign out if we can't verify role
          return;
        }

        // Check if user has superadmin role
        if (profile.is_superadmin === true) {
          // Save or clear email based on Remember Me checkbox
          if (rememberMe) {
            localStorage.setItem('superadminRememberedEmail', email);
          } else {
            localStorage.removeItem('superadminRememberedEmail');
          }

          // Super admin - redirect to superadmin dashboard
          console.log('✅ User is SuperAdmin, redirecting to /superadmin');
          navigate('/superadmin');
        } else if (profile.is_admin === true) {
          // Regular admin - redirect to admin dashboard instead
          console.log('⚠️ User is Admin but not SuperAdmin, redirecting to /admin');
          setError('You do not have Super Admin privileges. Redirecting to Admin panel...');
          setTimeout(() => {
            navigate('/admin');
          }, 2000);
        } else {
          // No admin privileges
          console.log('❌ User has no SuperAdmin privileges');
          setError('Access denied. You do not have Super Admin privileges.');
          await supabase.auth.signOut();
        }
      } else {
        setError('Authentication failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Super admin login error:', err);
      await supabase.auth.signOut();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <LoginCard>
        <Logo>W</Logo>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title>Super Admin</Title>
          <Badge>Highest Access Level</Badge>
        </div>
        <Subtitle>Welcome to WASL Super Admin Portal</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordInputWrapper>
              <PasswordInput
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <EyeIcon
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </EyeIcon>
            </PasswordInputWrapper>
            <RememberMeContainer>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                تذكرني - Remember Me
              </CheckboxLabel>
            </RememberMeContainer>
          </InputGroup>

          <Button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login to Super Admin'}
          </Button>
        </Form>

        <LinkContainer>
          <LinkText to="/">Back to Store</LinkText>
        </LinkContainer>
      </LoginCard>
    </Container>
  );
}

